<template>
  <v-stepper-content step="1" class="px-0 pt-4 pb-0">
    <section class="ds-form-section">
      <v-form ref="cpfFormInner" @submit.prevent="$emit('check-cpf')">
        <div class="ds-form-section__head">
          <div class="ds-form-section__title">
            <v-icon small color="primary" class="mr-2">mdi-card-account-details-outline</v-icon>
            <span>Informe o CPF</span>
          </div>
        </div>
        <div class="text-body-2 grey--text mb-3">
          Usamos o CPF para localizar ou criar o cadastro do paciente.
        </div>

        <v-text-field
          :value="cpfInput"
          label="CPF"
          outlined
          dense
          clearable
          hide-details="auto"
          :rules="cpfRules"
          v-mask="'###.###.###-##'"
          @input="$emit('update:cpf-input', $event)"
        />

        <v-btn
          v-if="!cpfChecked || !cpfExists"
          type="submit"
          color="primary"
          class="mt-3"
          block
          elevation="0"
          :loading="loadingCpf"
          :disabled="loadingCpf || onlyDigits(cpfInput).length !== 11"
        >
          Continuar
        </v-btn>

        <template v-if="cpfChecked && cpfExists">
          <v-alert type="info" dense outlined class="mt-3 mb-3">
            <div class="d-flex flex-column">
              <div>
                CPF já cadastrado
                <span v-if="existingPatientName">— {{ existingPatientName }}</span>.
              </div>

              <!-- ✅ Task 3: avisa quando o CPF pertence a um profissional/assistente -->
              <div v-if="existingUserRoleLabel" class="mt-1 caption">
                <v-icon small color="primary" class="mr-1">mdi-information-outline</v-icon>
                Este CPF pertence a um <b>{{ existingUserRoleLabel }}</b> já cadastrado no sistema.
                Ele será vinculado como paciente, mantendo seu cadastro original.
              </div>

              <div class="mt-1">
                <span v-if="shouldShowClinicSelectionOnCpfStep">
                  Selecione as clínicas e clique em <b>Vincular</b>.
                </span>
                <span v-else>
                  Clique em <b>Vincular</b> para associar este paciente.
                </span>
              </div>
            </div>
          </v-alert>

          <div v-if="shouldShowClinicSelectionOnCpfStep">
            <div class="text-subtitle-2 font-weight-medium mb-2">Clínicas</div>

            <v-select
              :value="selectedClinics"
              :items="clinics"
              item-text="name"
              item-value="id"
              label="Selecione as clínicas"
              multiple
              chips
              small-chips
              dense
              outlined
              hide-details="auto"
              :rules="clinicRules"
              @change="$emit('update:selected-clinics', $event)"
            >
              <template v-slot:item="{ item, attrs, on }">
                <v-list-item v-bind="attrs" v-on="on">
                  <v-list-item-action>
                    <v-checkbox
                      :input-value="selectedClinics.includes(item.id)"
                      :ripple="false"
                    />
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>{{ item.name }}</v-list-item-title>
                    <v-list-item-subtitle class="grey--text">
                      CNPJ: {{ item.cnpj }} — {{ item.city }}/{{ item.state }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </template>

              <template v-slot:selection="{ item, index }">
                <v-chip v-if="index < 3" small class="ma-1">{{ item.name }}</v-chip>
                <span v-else-if="index === 3" class="grey--text caption">
                  +{{ selectedClinics.length - 3 }} outras
                </span>
              </template>
            </v-select>
          </div>

          <v-btn
            color="primary"
            class="mt-3"
            block
            elevation="0"
            :loading="loadingCpf"
            :disabled="loadingCpf || (shouldShowClinicSelectionOnCpfStep && selectedClinics.length === 0)"
            @click="$emit('link-existing-patient')"
          >
            Vincular paciente
          </v-btn>

          <v-alert
            v-if="existingAlreadyLinked"
            type="warning"
            dense
            outlined
            class="mt-3"
          >
            Este paciente já está vinculado a você.
            <span v-if="shouldShowClinicSelectionOnCpfStep">
              Você pode selecionar clínicas e clicar em Vincular para garantir o vínculo.
            </span>
          </v-alert>
        </template>

        <v-alert v-if="erro" type="error" dense outlined class="mt-3 mb-0">
          {{ erro }}
        </v-alert>
      </v-form>
    </section>
  </v-stepper-content>
</template>

<script>
export default {
  name: "AddNewUserDialogCpfStep",

  props: {
    cpfInput: { type: String, default: "" },
    cpfChecked: { type: Boolean, default: false },
    cpfExists: { type: Boolean, default: false },
    existingPatientName: { type: String, default: "" },
    existingAlreadyLinked: { type: Boolean, default: false },
    existingUserRoleId: { type: Number, default: null }, // ✅ Task 3
    shouldShowClinicSelectionOnCpfStep: { type: Boolean, default: false },
    clinics: { type: Array, default: () => [] },
    selectedClinics: { type: Array, default: () => [] },
    loadingCpf: { type: Boolean, default: false },
    erro: { type: String, default: "" },
    onlyDigits: { type: Function, required: true },
  },

  computed: {
    cpfRules() {
      return [
        (v) => !!v || "CPF obrigatório",
        (v) => this.onlyDigits(v).length === 11 || "CPF deve ter 11 dígitos",
      ];
    },
    clinicRules() {
      return [(v) => (Array.isArray(v) && v.length > 0) || "Selecione ao menos uma clínica"];
    },
    // ✅ Task 3: descreve qual o papel principal do usuário existente
    existingUserRoleLabel() {
      if (this.existingUserRoleId === 1) return "profissional de saúde";
      if (this.existingUserRoleId === 3) return "assistente";
      return null; // role 2 (paciente) ou null = não exibe nada
    },
  },

  methods: {
    validate() {
      return this.$refs.cpfFormInner?.validate() ?? false;
    },
    resetValidation() {
      this.$refs.cpfFormInner?.resetValidation?.();
    },
  },
};
</script>